import gsap from "gsap";
import { BitmapText } from "pixi.js";
import App from "../../../vendor_core/core/src/App";
import PointerEvents from "../../../vendor_core/core/src/const/PointerEvents";
import BetModel from "../../../vendor_core/core/src/model/BetModel";
import SpinModel from "../../../vendor_core/core/src/model/SpinModel";
import { UIComponents } from "../../../vendor_core/core/src/ui/UIComponents";
import { DefaultLayers } from "../../../vendor_core/core/src/utils/Layers";
import Layout from "../../../vendor_core/core/src/utils/Layout";
import ViewManager from "../../../vendor_core/core/src/utils/ViewManager";
import BURJackpotsModel from "../models/BURJackpotsModel";
import { Fragment } from "../types/IBURJackpotsModel";
import BURReelsModel from "../models/BURReelsModel";
import { MaybeUndefined } from "../types/BURGameTypes";
import BURReelSymbol from "./BURReelSymbol";
import { GameLayers } from "../enums/GameLayers";

const JACKPOT_WIN_DURATION = 4;

/**
 * Plays the jackpots: filling the tiers with the fragments that were revealed, and the celebration when a tier is
 * completed. What is won and how far each tier is lives in the BURJackpotsModel, which this only reads and updates.
 */
class BURJackpotsView {
    private winBitmap: MaybeUndefined<BitmapText> = undefined;

    public async playJackpotsProgress(
        fragments: Fragment[] = BURJackpotsModel.jackpot_fragments
    ) {
        for (let fragment of fragments) {
            const lowerCaseTier = fragment.tier.toLowerCase();
            const fragmentSpine = BURReelsModel.reels.getSymbolAt(
                fragment.x,
                fragment.y
            ) as BURReelSymbol;
            const jpPlate = ViewManager.getLayer(GameLayers.JackpotPlates)
                .getCurrentScene()
                .spineAnimations.get(`${lowerCaseTier}JackpotPlate`)!;

            // Increment the current tier progress
            BURJackpotsModel.incrementJackpotProgress(fragment.tier);
            const progress = BURJackpotsModel.jackpots_progress[fragment.tier];

            // Play animations
            jpPlate.playAsync(`${lowerCaseTier}_win${progress}`);
            await fragmentSpine.playAsync("win");
            jpPlate.play(`${lowerCaseTier}_idle_full${progress}`, true);

            // Award the jackpot if threshold is reached
            if (
                progress ===
                BURJackpotsModel.jackpots_config![fragment.tier].threshold
            ) {
                await this.showJackpotWin(lowerCaseTier);
                BURJackpotsModel.resetJackpotProgress(fragment.tier);
                jpPlate.play(undefined, true);
                this.updateAccumulatedWin();
            }
        }

        await Promise.all(
            fragments.map(async (fragment) => {
                await BURReelsModel.reels.transformJackpotFragment(
                    {
                        x: fragment.x,
                        y: fragment.y
                    },
                    fragment.symbolId
                );
            })
        );
    }

    private async showJackpotWin(tier: string) {
        ViewManager.getLayer(DefaultLayers.SplashOverlay).setScene("open");
        const jpWinsScene = ViewManager.getLayer(
            GameLayers.JackpotWin
        ).setScene("open", undefined, true);
        const startAnim = Layout.isLandscape() ? "start" : "start_pt";
        const endAnim = Layout.isLandscape() ? "end" : "end_pt";
        const winSpine = jpWinsScene.spineAnimations.get("jackpotWins")!;
        winSpine.setAnimationSkin(tier);
        const loopTween = gsap.to(winSpine, {
            duration: JACKPOT_WIN_DURATION,
            onStart: () => {
                winSpine.playAsync(
                    Layout.isLandscape() ? "loop" : "loop_pt",
                    undefined,
                    true
                );
            },
            onComplete: () => {
                jpWinsScene.interactive = false;
            },
            paused: true
        });
        const winBitmap = this.getWinBitmap();
        const { winAmount, winCounter, scaleExponential, maxScale } =
            this.getWinConfig(tier.toUpperCase());

        BURJackpotsModel.setCurrentJackpotWin(winAmount);

        const winCountTween = gsap.to(winCounter, {
            duration:
                JACKPOT_WIN_DURATION + winSpine.getAnimDuration(startAnim),
            value: winAmount,
            onUpdate: () => {
                winBitmap.text = winCounter.value.toFixed(2).toString();
                if (winBitmap.scale.x < maxScale) {
                    winBitmap.scale.set(winBitmap.scale.x + scaleExponential);
                }
            },
            onComplete: () => {
                winBitmap.text = winCounter.value.toFixed(2).toString();
                if (winBitmap.scale.x < maxScale) {
                    winBitmap.scale.set(winBitmap.scale.x + scaleExponential);
                }
            }
        });

        winSpine.addChild(winBitmap);

        await winSpine.playAsync(startAnim);

        jpWinsScene.interactive = true;

        const evHandler = () => {
            jpWinsScene.interactive = false;
            winCountTween.progress(1);
            if (loopTween.isActive()) {
                loopTween.pause();
                gsap.delayedCall(1, async () => {
                    loopTween.progress(1);
                });
            }
        };

        jpWinsScene.once(PointerEvents.DOWN, evHandler);

        try {
            await loopTween.play();
            await gsap.to(winSpine, {
                duration: winSpine.getAnimDuration(endAnim) * 1.5,
                delay: 0.5,
                onStart: () => {
                    winSpine.playAsync(endAnim).then(() => {
                        this.onJackpotWinCompleted();
                        winSpine.removeChild(winBitmap);
                    });
                }
            });
        } finally {
            jpWinsScene.off(PointerEvents.DOWN, evHandler);
        }
    }

    private onJackpotWinCompleted() {
        ViewManager.getLayer(DefaultLayers.SplashOverlay).completeClear();
        ViewManager.getLayer(GameLayers.JackpotWin).clear();
    }

    private getWinBitmap(): BitmapText {
        if (!this.winBitmap || App.isObjectDestroyed(this.winBitmap)) {
            this.winBitmap = this.createBitmapWin();
        }
        this.winBitmap.text = "0";
        this.winBitmap.scale.set(1);
        return this.winBitmap;
    }

    private createBitmapWin() {
        const win = new BitmapText("0", {
            fontName: "jackpot_font",
            letterSpacing: -50,
            fontSize: 168
        });
        win.anchor.set(0.5);
        win.y = 150;
        return win;
    }

    private updateAccumulatedWin() {
        const scene = ViewManager.getLayer(
            DefaultLayers.BetBar
        ).getCurrentScene();
        const win = scene.sceneMoneyFields.get(UIComponents.Win);
        const winAlt = scene.sceneMoneyFields.get(UIComponents.WinAlternative);
        const jackpotWin = BURJackpotsModel.current_jackpot_win;
        if (!App.config.disableAltCurrency) {
            if (win) {
                win.value += jackpotWin;
            }
        }
        if (winAlt) {
            winAlt.value += jackpotWin;
        }
        SpinModel.accumulatedWin += jackpotWin;
    }

    private getWinConfig(tier: string) {
        const winCounter = { value: 0 };
        const winMultiplier = BURJackpotsModel.jackpots_config![tier].value;
        const winAmount = winMultiplier * BetModel.totalBet;
        const scaleExponential = winMultiplier / 9000;
        const maxScale = 1.8;

        return { winAmount, winCounter, scaleExponential, maxScale };
    }
}

export default new BURJackpotsView();
